import { Link } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { getCompetitionsApi } from '@/lib/api/competitionsApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateAbbreviatedFr, getInitials } from '@shared';
import type { AuthUser } from '@shared';
import { CompetitionStatus } from '@shared/enums/domain.enum';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Building2, Calendar, ChevronRight, ExternalLink, LayoutDashboard, Trophy } from 'lucide-react';

const ADMIN_URL = (import.meta.env.VITE_URL_ADMIN as string | undefined) ?? 'http://localhost:5231';

export function OrganizerQuickActions({ user }: { user: AuthUser }) {
  const firstOrg = user.organizationsAdmin?.[0]?.organization;

  return (
    <div className='mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3'>
      <a
        href={`${ADMIN_URL}/organizer/dashboard`}
        className='group flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm border border-slate-100 transition hover:border-indigo-200 hover:shadow-md'
      >
        <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 transition group-hover:bg-indigo-100'>
          <LayoutDashboard className='h-6 w-6' />
        </div>
        <div className='flex-1 min-w-0'>
          <p className='font-bold text-slate-900'>Tableau de bord</p>
          <p className='text-xs text-slate-500'>Gérer mon organisation</p>
        </div>
        <ExternalLink className='h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-indigo-500' />
      </a>

      <a
        href={`${ADMIN_URL}/organizer/competitions`}
        className='group flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm border border-slate-100 transition hover:border-blue-200 hover:shadow-md'
      >
        <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 transition group-hover:bg-blue-100'>
          <Trophy className='h-6 w-6' />
        </div>
        <div className='flex-1 min-w-0'>
          <p className='font-bold text-slate-900'>Mes concours</p>
          <p className='text-xs text-slate-500'>Créer et gérer</p>
        </div>
        <ExternalLink className='h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-blue-500' />
      </a>

      {firstOrg ? (
        <Link
          to={ROUTES.ORGANIZATION_PROFILE.replace(':id', String(firstOrg.id))}
          className='group flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm border border-slate-100 transition hover:border-emerald-200 hover:shadow-md'
        >
          <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 transition group-hover:bg-emerald-100'>
            <Building2 className='h-6 w-6' />
          </div>
          <div className='flex-1 min-w-0'>
            <p className='font-bold text-slate-900'>Ma page publique</p>
            <p className='text-xs text-slate-500'>Voir le profil organisation</p>
          </div>
          <ArrowRight className='h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-emerald-500 group-hover:translate-x-0.5' />
        </Link>
      ) : (
        <Link
          to={ROUTES.SEARCH_CONCOURS}
          className='group flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm border border-slate-100 transition hover:border-emerald-200 hover:shadow-md'
        >
          <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 transition group-hover:bg-emerald-100'>
            <Calendar className='h-6 w-6' />
          </div>
          <div className='flex-1 min-w-0'>
            <p className='font-bold text-slate-900'>Explorer les concours</p>
            <p className='text-xs text-slate-500'>Voir les concours publiés</p>
          </div>
          <ArrowRight className='h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-emerald-500 group-hover:translate-x-0.5' />
        </Link>
      )}
    </div>
  );
}

export function OrganizerOrganizations({ user }: { user: AuthUser }) {
  const orgs = user.organizationsAdmin ?? [];
  if (!orgs.length) return null;

  return (
    <div className='mb-6 rounded-2xl bg-white border border-slate-100 shadow-sm p-6'>
      <h2 className='mb-4 flex items-center gap-2 text-base font-bold text-slate-900'>
        <Building2 className='h-4 w-4 text-indigo-500' />
        {orgs.length > 1 ? 'Mes organisations' : 'Mon organisation'}
      </h2>
      <div className='space-y-3'>
        {orgs.map(membership => (
          <Link
            key={membership.organizationId}
            to={ROUTES.ORGANIZATION_PROFILE.replace(':id', String(membership.organizationId))}
            className='group flex items-center gap-3 rounded-xl bg-slate-50 p-3 transition hover:bg-slate-100'
          >
            <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-linear-to-br from-indigo-500 to-purple-600 text-xs font-bold text-white'>
              {getInitials(membership.organization.name)}
            </div>
            <div className='flex-1 min-w-0'>
              <p className='truncate text-sm font-bold text-slate-900'>{membership.organization.name}</p>
              <p className='text-xs text-slate-500'>
                Plan : <span className='font-medium'>{membership.organization.plan}</span>
              </p>
            </div>
            <ChevronRight className='h-4 w-4 shrink-0 text-slate-400 transition group-hover:text-indigo-500' />
          </Link>
        ))}
      </div>
    </div>
  );
}

export function OrganizerRecentCompetitions({ user }: { user: AuthUser }) {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const firstOrgId = user.organizationsAdmin?.[0]?.organizationId;

  const { data: competitionsData, isLoading } = useQuery({
    queryKey: queryKeys.competitions.list({ organizationId: firstOrgId, limit: 5, sortBy: 'date', sortOrder: 'desc' }),
    queryFn: () =>
      getCompetitionsApi({
        organizationId: firstOrgId,
        limit: 5,
        sortBy: 'date',
        sortOrder: 'desc',
      }),
    enabled: isAuthenticated && !!firstOrgId,
  });

  const competitions = competitionsData?.data ?? [];

  return (
    <div className='mb-6 rounded-2xl bg-white border border-slate-100 shadow-sm p-6'>
      <div className='mb-5 flex items-center justify-between'>
        <h2 className='flex items-center gap-2 text-base font-bold text-slate-900'>
          <Trophy className='h-4 w-4 text-blue-500' />
          Concours récents
        </h2>
        <a
          href={`${ADMIN_URL}/organizer/competitions`}
          className='inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700'
        >
          Voir tout <ExternalLink className='h-3 w-3' />
        </a>
      </div>

      {isLoading ? (
        <div className='space-y-3'>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className='h-14 animate-pulse rounded-xl bg-slate-100' />
          ))}
        </div>
      ) : competitions.length === 0 ? (
        <div className='flex flex-col items-center gap-3 py-8 text-center'>
          <Trophy className='h-10 w-10 text-slate-200' />
          <p className='text-sm text-slate-400 font-medium'>Aucun concours créé pour l&apos;instant.</p>
          <a
            href={`${ADMIN_URL}/organizer/competitions/new`}
            className='inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700 transition-colors'
          >
            Créer un concours <ArrowRight className='h-4 w-4' />
          </a>
        </div>
      ) : (
        <div className='divide-y divide-slate-50'>
          {competitions.map(comp => (
            <div key={comp.id} className='flex items-center gap-4 py-3 first:pt-0 last:pb-0'>
              <div className='flex-1 min-w-0'>
                <Link
                  to={`/concours/${String(comp.id)}`}
                  className='truncate text-sm font-bold text-slate-900 hover:text-blue-600'
                >
                  {comp.name}
                </Link>
                <div className='mt-0.5 flex flex-wrap gap-x-3 text-xs text-slate-400'>
                  <span>{comp.date ? formatDateAbbreviatedFr(comp.date) : '—'}</span>
                  {comp.city && <span>{comp.city}</span>}
                </div>
              </div>
              <span
                className={`shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
                  comp.status === CompetitionStatus.REGISTRATION_OPEN
                    ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                    : comp.status === CompetitionStatus.FINISHED
                      ? 'bg-slate-50 text-slate-500 border-slate-200'
                      : 'bg-amber-50 text-amber-600 border-amber-200'
                }`}
              >
                {comp.status === CompetitionStatus.REGISTRATION_OPEN
                  ? 'Inscriptions ouvertes'
                  : comp.status === CompetitionStatus.FINISHED
                    ? 'Terminé'
                    : comp.status === CompetitionStatus.DRAFT
                      ? 'Brouillon'
                      : comp.status === CompetitionStatus.REGISTRATION_CLOSED
                        ? 'Inscriptions fermées'
                        : comp.status === CompetitionStatus.IN_PROGRESS
                          ? 'En cours'
                          : comp.status === CompetitionStatus.CANCELLED
                            ? 'Annulé'
                            : comp.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
